import { useState, useEffect, useRef } from 'react';
import {
  formatDateString,
  parseRangeString,
  computeInitialDates,
  computeMinMaxDates,
  computeStaticRanges,
  clampSelectionToDateThresholdsAndMaxSpan,
  getSundayOfWeek,
  getSaturdayOfWeek,
} from './dateRangeUtils';

/**
 * Custom hook to encapsulate outside click events.
 */
const useClickOutside = (ref, onOutsideClick) => {
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onOutsideClick();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [ref, onOutsideClick]);
};

/**
 * Custom hook to manage date range selection state, validation/clamping conditions, and popover state.
 */
export const useDateRangePicker = ({
  startDate,
  endDate,
  value,
  onChange,
  disableFutureDates = false,
  disablePastDates = false,
  disablePastDateByWeekly = false,
  disablePastDatesByWeekly = false,
  disableMonthPresets = true,
  minDate: minDateThreshold,
  maxDate: maxDateThreshold,
  staticRanges: customStaticRanges,
  maxDays = 7,
  maxRangeDays,
  placeholder = 'Select Date Range',
  defaultToWeekToday = false,
  defaultToPastWeek = false,
  snapToWeek = false,
}) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const isWeeklyMode = snapToWeek || defaultToWeekToday || defaultToPastWeek;

  const initialDates = computeInitialDates({
    startDate,
    endDate,
    value,
    defaultToPastWeek: isWeeklyMode,
    minDateThreshold,
  });

  const [selectedRange, setSelectedRange] = useState([
    {
      startDate: initialDates.start,
      endDate: initialDates.end,
      key: 'selection',
    },
  ]);

  const [draftRange, setDraftRange] = useState(selectedRange);

  const { minDate, maxDate } = computeMinMaxDates({
    disablePastDates,
    disablePastDateByWeekly,
    disablePastDatesByWeekly,
    disableFutureDates,
    minDateThreshold,
    maxDateThreshold,
  });

  const effectiveStaticRanges = computeStaticRanges(disableMonthPresets, customStaticRanges);

  // Sync external changes (startDate, endDate, value)
  useEffect(() => {
    if (startDate && endDate) {
      const parsedStart = new Date(startDate);
      const parsedEnd = new Date(endDate);
      if (!isNaN(parsedStart.getTime()) && !isNaN(parsedEnd.getTime())) {
        const newRange = [{ startDate: parsedStart, endDate: parsedEnd, key: 'selection' }];
        setSelectedRange(newRange);
        setDraftRange(newRange);
      }
    } else {
      const parsedRange = parseRangeString(value);
      if (parsedRange) {
        const newRange = [{ startDate: parsedRange.startDate, endDate: parsedRange.endDate, key: 'selection' }];
        setSelectedRange(newRange);
        setDraftRange(newRange);
      }
    }
  }, [value, startDate, endDate]);

  // Adjust selection if minimum date threshold changes
  const minDateThresholdTimestamp =
    minDateThreshold instanceof Date && !isNaN(minDateThreshold.getTime())
      ? minDateThreshold.getTime()
      : null;

  useEffect(() => {
    if (minDateThresholdTimestamp) {
      const activeMinThresholdDate = new Date(minDateThresholdTimestamp);
      setSelectedRange((previousRange) => {
        const currentSelection = previousRange[0];
        const isStartBeforeMinThreshold =
          currentSelection?.startDate && currentSelection.startDate < activeMinThresholdDate;
        if (isStartBeforeMinThreshold) {
          const clampedSelection = { ...currentSelection, startDate: activeMinThresholdDate };
          setDraftRange([clampedSelection]);
          return [clampedSelection];
        }
        return previousRange;
      });
    }
  }, [minDateThresholdTimestamp]);

  const handleConfirmSelection = () => {
    if (isOpen) {
      const confirmedSelection = draftRange[0] || {};
      setSelectedRange(draftRange);
      setIsOpen(false);

      if (confirmedSelection.startDate && confirmedSelection.endDate && onChange) {
        const formattedStartDate = formatDateString(confirmedSelection.startDate);
        const formattedEndDate = formatDateString(confirmedSelection.endDate);
        const formattedDateRange = `${formattedStartDate} - ${formattedEndDate}`;

        onChange({
          startDate: confirmedSelection.startDate,
          endDate: confirmedSelection.endDate,
          formattedStartDate,
          formattedEndDate,
          startStr: formattedStartDate,
          endStr: formattedEndDate,
          rangeString: formattedDateRange,
          value: formattedDateRange,
        });
      }
    }
  };

  useClickOutside(containerRef, () => {
    if (isOpen) {
      handleConfirmSelection();
    }
  });

  const handleDraftSelectionChange = (pickerRangeItem) => {
    let selection = { ...pickerRangeItem.selection };

    if (snapToWeek) {
      const start = selection.startDate ? getSundayOfWeek(selection.startDate) : getSundayOfWeek(new Date());
      const end = selection.endDate ? getSaturdayOfWeek(selection.endDate) : getSaturdayOfWeek(start);
      selection = {
        ...selection,
        startDate: start,
        endDate: end,
      };
    }

    const maxAllowedSpanInDays = maxRangeDays || maxDays;
    selection = clampSelectionToDateThresholdsAndMaxSpan(
      selection,
      maxAllowedSpanInDays,
      maxDate,
      minDate
    );

    setDraftRange([selection]);
  };

  const handleTogglePopover = () => {
    if (!isOpen) {
      setDraftRange(selectedRange);
      setIsOpen(true);
    } else {
      handleConfirmSelection();
    }
  };

  const activeRange = (isOpen ? draftRange[0] : selectedRange[0]) || {};
  const displayLabel =
    activeRange.startDate && activeRange.endDate
      ? `${formatDateString(activeRange.startDate)} - ${formatDateString(activeRange.endDate)}`
      : value || placeholder;

  return {
    containerRef,
    isOpen,
    draftRange,
    minDate,
    maxDate,
    effectiveStaticRanges,
    displayLabel,
    handleTogglePopover,
    handleConfirmSelection,
    handleDraftSelectionChange,
  };
};

export default useDateRangePicker;
